import { useQuery } from '@tanstack/react-query';
import { errorMessage, fetchSiteChanges, SiteChange, SiteChangeKind } from './api';

const dateTimeFmt = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const KIND_LABEL: Record<SiteChangeKind, string> = {
  added: 'Aggiunto',
  removed: 'Rimosso',
  changed: 'Modificato',
};

function readableDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : dateTimeFmt.format(date);
}

function ChangeValues({ change }: { change: SiteChange }) {
  if (change.highConfidence && change.field === 'techPixels') {
    return <p className="site-change-detected">{change.after ?? 'Configurazione di tracking modificata'}</p>;
  }

  return (
    <div className="site-change-values">
      <div><span>Prima</span><p>{change.before || 'Non presente'}</p></div>
      <span aria-hidden="true">→</span>
      <div><span>Dopo</span><p>{change.after || 'Non presente'}</p></div>
    </div>
  );
}

export default function SiteChanges({ pageId, enabled }: { pageId: string; enabled: boolean }) {
  const changes = useQuery({
    queryKey: ['site-changes', pageId],
    queryFn: () => fetchSiteChanges(pageId),
    enabled,
    staleTime: 60_000,
  });

  if (!enabled) return null;

  if (changes.isPending) {
    return <div className="site-changes-loading skeleton" role="status"><span className="sr-only">Controllo modifiche del sito</span><div className="sk-line sk-w40" /><div className="sk-line" /></div>;
  }

  if (changes.isError) {
    return <div className="site-changes-error"><p className="error">{errorMessage(changes.error)}</p><button type="button" className="link-btn" onClick={() => changes.refetch()}>Riprova confronto</button></div>;
  }

  const result = changes.data;
  if (result.snapshotCount === 0) return null;

  if (!result.hasComparison) {
    return <p className="site-change-baseline"><strong>Baseline salvata.</strong> Serve un secondo aggiornamento del sito per rilevare prezzi, funzionalità e tracking modificati.</p>;
  }

  const previousAt = readableDate(result.previousAt);
  const currentAt = readableDate(result.currentAt);

  return (
    <section className="site-changes" aria-labelledby="site-changes-title">
      <div className="site-changes-head">
        <div><h5 id="site-changes-title">Cosa è cambiato</h5>{previousAt && currentAt && <small>{previousAt} → {currentAt}</small>}</div>
        <span className={result.contentChanged ? 'change-status changed' : 'change-status'}>{result.contentChanged ? 'Pagina modificata' : 'Contenuto stabile'}</span>
      </div>

      {result.changes.length === 0 ? (
        <p className="muted">{result.contentChanged ? 'La pagina è cambiata, ma prezzi, offerta e tecnologie estratte risultano stabili.' : 'Nessuna modifica rilevata nei segnali confrontabili.'}</p>
      ) : (
        <div className="site-change-list">
          {result.changes.map((change) => (
            <article className="site-change-row" key={change.field}>
              <header><strong>{change.label}</strong><span className={`change-kind ${change.kind}`}>{KIND_LABEL[change.kind]}</span><span className={change.highConfidence ? 'evidence-badge detected' : 'evidence-badge inferred'}>{change.highConfidence ? 'Rilevato' : 'Sintesi AI'}</span></header>
              <ChangeValues change={change} />
            </article>
          ))}
        </div>
      )}

      {result.changes.some((change) => !change.highConfidence) && <p className="site-change-note">Le variazioni di testo confrontano sintesi AI: usa il prima e dopo per confermare il significato. Titolo e pixel sono rilevati direttamente.</p>}
    </section>
  );
}
